import { CO_SUPERVISOR_GROUPS } from "variables/constants";

export const DIGITAL_VIEW_KEYS = ["email", "live-chat", "sms"];

const TITLE_CASE_MINOR = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "for",
  "to",
  "in",
  "on",
  "of",
  "by",
  "vs",
  "with",
]);

/** Title-case dashboard labels (keeps acronyms like SMS/KPI). */
export const toTitleCaseLabel = (str) => {
  if (!str || typeof str !== "string") return str;
  const words = str.trim().split(/\s+/);
  return words
    .map((w, i) => {
      if (/^[A-Z0-9]{2,}$/.test(w)) return w;
      const lower = w.toLowerCase();
      if (i > 0 && TITLE_CASE_MINOR.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

export const DIGITAL_SENTIMENT_PILL_CLASS = {
  happy: "bg-[#DDF5E7] text-[#1B7F4E]",
  positive: "bg-[#DDF5E7] text-[#1B7F4E]",
  neutral: "bg-[#E8E8E8] text-gray-900",
  unhappy: "bg-[#FFE8E8] text-[#EF4444]",
  negative: "bg-[#FFE8E8] text-[#EF4444]",
};

export const DIGITAL_CHANNEL_ACCENTS = {
  email: "#3B82F6",
  live_chat: "#14B8A6",
  sms: "#8B5CF6",
};

const normalizeNameKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

/** True when two supervisor names are the same or share a co-manager group. */
export const coSupervisorsMatch = (supervisorName, rowSupervisor) => {
  const a = normalizeNameKey(supervisorName);
  const b = normalizeNameKey(rowSupervisor);
  if (!a || !b) return false;
  if (a === b) return true;
  return (CO_SUPERVISOR_GROUPS || []).some((group) => {
    const keys = group.map(normalizeNameKey);
    return keys.includes(a) && keys.includes(b);
  });
};

/**
 * Digital QA visibility for a supervisor: roster match or co-manager on
 * row.supervisor (mirrors voice GREAT in index.jsx).
 */
export const isDigitalRowVisibleToSupervisor = (supervisorName, row, teamMemberNameKeys) => {
  const advisorKey = normalizeNameKey(row?.advisor_name);
  if (teamMemberNameKeys?.has(advisorKey)) return true;
  return coSupervisorsMatch(supervisorName, row?.supervisor);
};

const advisorDirectoryByName = new Map(
  [
    ["Michelle Araya", "Customer Service"],
    ["Victoria Gaynor", "Customer Service"],
    ["Willie Nunes", "Customer Service"],
    ["Alexandra Lynn", "Customer Service"],
    ["Lisa Jones", "Customer Service"],
    ["Walid Azar", "Customer Service"],
    ["Natasha Doobay", "Customer Service"],
    ["Darline Nunez", "Customer Service"],
    ["Elizabeth Hernandez", "Customer Service"],
    ["Sevag Tashjian", "Customer Service"],
    ["Anthony Saliba", "Customer Service"],
    ["Heather Poppy", "Custom Order"],
    ["Tracy Verry", "Custom Order"],
    ["Samer Francis", "Custom Order"],
    ["Vrej Kendoyan", "Custom Order"],
    ["Joe Chahwan", "Custom Order"],
    ["Jasmine Almayan", "Concierge"],
    ["Vartan Kaloustian", "Concierge"],
    ["Janelle Fernuik", "Account Executive"],
    ["Joelle Jreije", "Account Executive"],
    ["Razmig AlMayan", "Account Executive"],
    ["Inderjit Singh", "Account Executive"],
    ["Belma Yucel", "Account Executive"],
    ["Rachel Hayduk", "Account Executive"],
    ["Emma Lanka", "Account Executive"],
    ["John Bader", "Custom Order"],
    ["Joyce Dagher", "Custom Order"],
  ].map(([name, dept]) => [normalizeNameKey(name), dept])
);

/** Merge near-duplicate reason labels (trailing punctuation, spacing). */
export const canonicalizeInteractionReason = (reason) =>
  String(reason || "")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,;:!?\s]+$/g, "");

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatMonthPeriodFromDate = (date) => {
  if (!date) return null;
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
};

export const getRowMonthPeriod = (row) => {
  const d = parseInteractionDate(row?.interaction_date);
  return formatMonthPeriodFromDate(d);
};

/** Invisible x-axis slot so a single-month line chart centers on its label. */
const LINE_CHART_CENTER_PAD = "\u200B";

/**
 * With one category ApexCharts pins the marker on the left edge; pad with blank
 * slots so the dot and month label share the same x position.
 */
export const buildCenteredCategoryLineChart = (periods, values) => {
  if (periods.length !== 1) {
    return { categories: periods, values };
  }
  const [period] = periods;
  const [value] = values;
  return {
    categories: [LINE_CHART_CENTER_PAD, period, LINE_CHART_CENTER_PAD],
    values: [null, value, null],
  };
};

export const formatLineChartCategoryLabel = (val) => {
  const s = String(val ?? "")
    .replace(/\u200B/g, "")
    .trim();
  return s || "";
};

/** Leading month with no score so the first real month is not on the y-axis. */
export const prependMissingLeadingMonth = (monthlyData) => {
  if (!monthlyData?.length) return monthlyData;
  const first = monthlyData[0];
  const match = String(first.period || "").match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (!match) return monthlyData;
  const monthIdx = MONTH_NAMES.indexOf(match[1]);
  const year = parseInt(match[2], 10);
  if (monthIdx < 0 || Number.isNaN(year)) return monthlyData;
  const prevDate = new Date(year, monthIdx - 1, 1);
  const prevPeriod = formatMonthPeriodFromDate(prevDate);
  if (!prevPeriod || monthlyData.some((d) => d.period === prevPeriod)) {
    return monthlyData;
  }
  return [
    {
      period: prevPeriod,
      date: prevDate,
      avgScore: 0,
      callCount: 0,
      leadingPad: true,
    },
    ...monthlyData,
  ];
};

const monthPeriodSortKey = (period) => {
  const m = String(period || "").match(/^([A-Za-z]{3})\s+(\d{4})$/);
  if (!m) return 0;
  const monthIdx = MONTH_NAMES.indexOf(m[1]);
  const year = parseInt(m[2], 10);
  if (monthIdx < 0 || Number.isNaN(year)) return 0;
  return year * 12 + monthIdx;
};

/** Calendar month label (fallback when no rows). */
export const digitalChartPeriods = () => {
  const now = new Date();
  return [formatMonthPeriodFromDate(now)];
};

/**
 * Chart months from actual QA rows (excludes August 2026). Uses the latest month
 * that has data so grade/individual charts are not empty when the calendar
 * month has not been scored yet.
 */
/** Grade pies for email / live chat / SMS always show these two months when requested. */
export const DIGITAL_GRADE_PIE_MONTHS = ["Sep 2026", "Oct 2026"];

export const digitalChartPeriodsFromRows = (rows, { maxMonths = 1, forcePeriods = null } = {}) => {
  if (Array.isArray(forcePeriods) && forcePeriods.length > 0) {
    return forcePeriods.slice(-maxMonths);
  }
  const set = new Set();
  (rows || []).forEach((row) => {
    if (isAugust2026Row(row)) return;
    const period = getRowMonthPeriod(row);
    if (period) set.add(period);
  });
  const sorted = Array.from(set).sort((a, b) => monthPeriodSortKey(a) - monthPeriodSortKey(b));
  if (!sorted.length) return digitalChartPeriods();
  if (maxMonths <= 1) return [sorted[sorted.length - 1]];
  return sorted.slice(-maxMonths);
};

export const getAdvisorDepartment = (row) => {
  const key = normalizeNameKey(row?.advisor_name);
  const fromRow = String(row?.department ?? row?.Department ?? "").trim();
  // Digital QA rows carry the scored department; use it when present.
  if (fromRow) return fromRow;
  return advisorDirectoryByName.get(key) || "";
};

/** User-visible department (directory + row field); never leave mapped CS reps blank. */
export const formatResolvedAdvisorDepartment = (row) => {
  const dept = getAdvisorDepartment(row);
  return dept || "Unmapped";
};

/** Canonical email Top Reasons (display order). */
export const EMAIL_TOP_REASONS = [
  "Place the order",
  "Status check and update",
  "Price Quotes",
  "Product Inquiry",
  "Follow ups",
  "Create Memos",
  "Image request",
  "Creating RA for Repairs and Return",
  "Rendering Approval - Factory to Retailer",
  "Internal Emails - Factory Questions on a order",
  "Internal Emails - Checking Price, Shipment, Finance etc",
];

export const EMAIL_TOP_REASONS_OTHERS_LABEL = "Others";

/** @deprecated use EMAIL_TOP_REASONS + EMAIL_TOP_REASONS_OTHERS_LABEL */
export const EMAIL_CANONICAL_REASONS = [...EMAIL_TOP_REASONS, EMAIL_TOP_REASONS_OTHERS_LABEL];

const normalizeReasonKey = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const padKey = (key) => ` ${key} `;

/** Raw reason text from a digital row (email may use interaction or call field). */
export const emailInteractionReasonRaw = (row) =>
  canonicalizeInteractionReason(row?.reason_for_interaction || row?.reason_for_call || "");

/** Regex rules on normalized reason text (order: most specific first). */
const EMAIL_REASON_REGEX_RULES = [
  {
    canon: "Internal Emails - Factory Questions on a order",
    test: (k) =>
      /factory questions on (a|an) order/.test(k) ||
      /internal emails factory/.test(k) ||
      /factory question/.test(k) ||
      /questions on (a|an) order/.test(k),
  },
  {
    canon: "Internal Emails - Checking Price, Shipment, Finance etc",
    test: (k) =>
      /internal emails checking/.test(k) ||
      /checking price shipment finance/.test(k) ||
      (/internal email/.test(k) && /(shipment|finance|checking price)/.test(k)),
  },
  {
    canon: "Rendering Approval - Factory to Retailer",
    test: (k) =>
      /rendering approval/.test(k) ||
      /factory to retailer/.test(k) ||
      /render approval/.test(k) ||
      (/render/.test(k) && /approv/.test(k)),
  },
  {
    canon: "Creating RA for Repairs and Return",
    test: (k) =>
      /to request a return/.test(k) ||
      /request a return/.test(k) ||
      /request return/.test(k) ||
      /return authorization/.test(k) ||
      /creating ra/.test(k) ||
      /repairs and return/.test(k) ||
      /repair and return/.test(k) ||
      /\bra for repair/.test(k) ||
      (/repair/.test(k) && /return/.test(k)),
  },
  {
    canon: "Place the order",
    test: (k) =>
      /to place an order/.test(k) ||
      /to place a order/.test(k) ||
      /place the order/.test(k) ||
      /place an order/.test(k) ||
      /place a order/.test(k) ||
      /placing an order/.test(k) ||
      /placing order/.test(k) ||
      /go to place an order/.test(k) ||
      /go to place/.test(k),
  },
  {
    canon: "Status check and update",
    test: (k) =>
      /status check and update/.test(k) ||
      /to track an order/.test(k) ||
      /to track a order/.test(k) ||
      /track an order/.test(k) ||
      /track order/.test(k) ||
      /order status/.test(k) ||
      /status check/.test(k) ||
      /status update/.test(k) ||
      /check status/.test(k) ||
      /shipping status/.test(k) ||
      /delivery status/.test(k) ||
      /where is my order/.test(k) ||
      /order update/.test(k),
  },
  {
    canon: "Price Quotes",
    test: (k) =>
      /to check pricing/.test(k) ||
      /check pricing/.test(k) ||
      /price quotes/.test(k) ||
      /price quote/.test(k) ||
      /pricing quote/.test(k) ||
      /quote request/.test(k) ||
      (/pricing/.test(k) && !/product inquiry/.test(k)),
  },
  {
    canon: "Product Inquiry",
    test: (k) =>
      /product inquiry/.test(k) ||
      /custom order inquiry/.test(k) ||
      /product question/.test(k) ||
      /product info/.test(k) ||
      /product information/.test(k) ||
      /inquiry about product/.test(k) ||
      /sku inquiry/.test(k) ||
      /item inquiry/.test(k),
  },
  {
    canon: "Follow ups",
    test: (k) =>
      /follow ups/.test(k) ||
      /follow up/.test(k) ||
      /followup/.test(k) ||
      /outbound follow/.test(k) ||
      /pricing follow/.test(k) ||
      /inquiry follow/.test(k) ||
      /image rendering follow/.test(k) ||
      /rendering follow/.test(k) ||
      /image follow/.test(k),
  },
  {
    canon: "Create Memos",
    test: (k) => /create memos/.test(k) || /create memo/.test(k) || /creating memo/.test(k) || /\bmemo\b/.test(k),
  },
  {
    canon: "Image request",
    test: (k) =>
      /image request/.test(k) ||
      /photo request/.test(k) ||
      /request image/.test(k) ||
      /requesting image/.test(k) ||
      /to request an image/.test(k) ||
      /request an image/.test(k) ||
      /request images/.test(k) ||
      (/image/.test(k) && /request/.test(k) && !/render/.test(k)),
  },
];

const EMAIL_REASON_TOKEN_RULES = [
  {
    canon: "Internal Emails - Factory Questions on a order",
    tokens: ["factory questions on a order", "factory questions on an order", "factory question on order"],
  },
  {
    canon: "Internal Emails - Checking Price, Shipment, Finance etc",
    tokens: ["checking price shipment finance", "checking price", "shipment finance", "internal emails checking"],
  },
  {
    canon: "Rendering Approval - Factory to Retailer",
    tokens: ["rendering approval", "factory to retailer", "render approval"],
  },
  {
    canon: "Creating RA for Repairs and Return",
    tokens: ["creating ra", "ra for repair", "return authorization", "request a return", "to request a return"],
  },
  {
    canon: "Place the order",
    tokens: ["to place an order", "to place a order", "place the order", "place an order", "placing an order", "go to place"],
  },
  {
    canon: "Status check and update",
    tokens: ["to track an order", "track an order", "track order", "order status", "status check", "status update"],
  },
  {
    canon: "Price Quotes",
    tokens: ["to check pricing", "check pricing", "price quote", "price quotes", "pricing quote"],
  },
  {
    canon: "Product Inquiry",
    tokens: ["product inquiry", "custom order inquiry", "product question", "product info"],
  },
  { canon: "Follow ups", tokens: ["follow ups", "follow up", "follow-up", "followup"] },
  { canon: "Create Memos", tokens: ["create memos", "create memo", "creating memo"] },
  { canon: "Image request", tokens: ["image request", "photo request", "request image"] },
];

const matchEmailReasonKey = (key) => {
  if (!key) return null;

  const exact = EMAIL_TOP_REASONS.find((c) => normalizeReasonKey(c) === key);
  if (exact) return exact;

  for (const { canon, test } of EMAIL_REASON_REGEX_RULES) {
    if (test(key)) return canon;
  }

  const padded = padKey(key);
  for (const { canon, tokens } of EMAIL_REASON_TOKEN_RULES) {
    if (tokens.some((t) => key.includes(t) || padded.includes(padKey(t)))) return canon;
  }

  if (/\bquote\b/.test(key) && !/inquiry/.test(key)) return "Price Quotes";
  if (/\brepair\b/.test(key) || /\breturn\b/.test(key)) return "Creating RA for Repairs and Return";
  if (/\btrack\b/.test(key) && /\border\b/.test(key)) return "Status check and update";
  if (/\bplace\b/.test(key) && /\border\b/.test(key)) return "Place the order";
  if (/\binquiry\b/.test(key)) return "Product Inquiry";

  return null;
};

export const canonicalizeEmailInteractionReason = (reason) => {
  const raw = canonicalizeInteractionReason(reason);
  if (!raw) return EMAIL_TOP_REASONS_OTHERS_LABEL;
  const key = normalizeReasonKey(raw);
  if (!key) return EMAIL_TOP_REASONS_OTHERS_LABEL;

  if (key.includes("/")) {
    const parts = key.split("/").map((p) => normalizeReasonKey(p)).filter(Boolean);
    for (const part of parts) {
      const mapped = matchEmailReasonKey(part);
      if (mapped) return mapped;
    }
  }

  return matchEmailReasonKey(key) || EMAIL_TOP_REASONS_OTHERS_LABEL;
};

/** Top Reasons chart data: canonical buckets with count >= 1 only (+ Others when unmapped). */
export const summarizeEmailTopReasonsForChart = (rows) => {
  const counts = Object.fromEntries(EMAIL_TOP_REASONS.map((label) => [label, 0]));
  let others = 0;
  (rows || []).forEach((row) => {
    const canon = canonicalizeEmailInteractionReason(emailInteractionReasonRaw(row));
    if (EMAIL_TOP_REASONS.includes(canon)) counts[canon] += 1;
    else others += 1;
  });
  const labels = [];
  const series = [];
  EMAIL_TOP_REASONS.forEach((label) => {
    const count = counts[label] || 0;
    if (count >= 1) {
      labels.push(label);
      series.push(count);
    }
  });
  if (others >= 1) {
    labels.push(EMAIL_TOP_REASONS_OTHERS_LABEL);
    series.push(others);
  }
  const total = (rows || []).length;
  return { labels, series, total };
};

export const normalizeDigitalChannel = (channel) => {
  const c = String(channel || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (c === "livechat") return "live_chat";
  return c;
};

export const LIVE_CHAT_DEPARTMENTS = ["Customer Service", "Custom Order"];

export const LIVE_CHAT_DEPARTMENT_FILTER_OPTIONS = [
  { value: "", label: "Customer Service & Custom Order" },
  { value: "Customer Service", label: "Customer Service" },
  { value: "Custom Order", label: "Custom Order" },
];

const isDeptOneOf = (row, departments) => {
  const dept = getAdvisorDepartment(row).toLowerCase();
  return departments.some((d) => d.toLowerCase() === dept);
};

/** Live chat QA includes Customer Service and Custom Order advisors. */
export const isLiveChatScopedRow = (row) => isDeptOneOf(row, LIVE_CHAT_DEPARTMENTS);

/** Live chat and SMS QA include Customer Service and Custom Order advisors. */
export const isSmsScopedRow = (row) => isLiveChatScopedRow(row);

/** @deprecated Use isLiveChatScopedRow or isSmsScopedRow */
export const isLiveChatOrSmsCsScopedRow = isLiveChatScopedRow;
export const isSmsCsScopedRow = isSmsScopedRow;

/** Audit helper: live_chat row counts by resolved department (YTD-style caller passes rows). */
export const auditLiveChatRowsByDepartment = (rows) => {
  const byDept = {};
  const byAdvisor = {};
  (rows || [])
    .filter((r) => normalizeDigitalChannel(r.channel) === "live_chat")
    .forEach((r) => {
      const dept = getAdvisorDepartment(r) || "(unmapped advisor)";
      byDept[dept] = (byDept[dept] || 0) + 1;
      const name = String(r.advisor_name || "(no name)").trim();
      byAdvisor[name] = (byAdvisor[name] || 0) + 1;
    });
  const sortedDepts = Object.entries(byDept).sort((a, b) => b[1] - a[1]);
  const sortedAdvisors = Object.entries(byAdvisor).sort((a, b) => b[1] - a[1]);
  return { byDept, sortedDepts, byAdvisor, sortedAdvisors };
};

export const filterDigitalRows = (rows, { departmentFilter, dateRange } = {}) => {
  const [start, end] = dateRange || [];
  const hasRange = start && end;
  return (rows || []).filter((row) => {
    if (departmentFilter && getAdvisorDepartment(row).toLowerCase() !== departmentFilter.toLowerCase()) {
      return false;
    }
    if (hasRange) {
      const d = parseInteractionDate(row?.interaction_date);
      if (!d) return false;
      const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const sOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const eOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      if (dOnly < sOnly || dOnly > eOnly) return false;
    }
    return true;
  });
};

export const viewKeyToDbChannel = (viewKey) => {
  if (viewKey === "live-chat") return "live_chat";
  return viewKey;
};

export const dbChannelToLabel = (channel) => {
  if (channel === "email") return "Email";
  if (channel === "live_chat") return "Live Chat";
  if (channel === "sms") return "SMS";
  return channel;
};

/** SMS is CS-only on Summary when another department is selected. Live chat also allows Custom Order. */
export const DIGITAL_CS_ONLY_VIEW_KEYS = new Set(["sms"]);

export const isDigitalSummaryChannelVisible = (viewKey, departmentFilter) => {
  if (viewKey === "summary" || viewKey === "inbound" || viewKey === "outbound" || viewKey === "email") {
    return true;
  }
  const dept = String(departmentFilter || "").trim().toLowerCase();
  if (viewKey === "live-chat") {
    if (!dept) return true;
    return dept === "customer service" || dept === "custom order";
  }
  if (viewKey === "sms") {
    if (!dept) return true;
    return dept === "customer service" || dept === "custom order";
  }
  return true;
};

export const digitalViewBadgeLabel = (viewKey) => {
  if (viewKey === "email") return "Emails";
  if (viewKey === "live-chat") return "Live Chats";
  if (viewKey === "sms") return "SMS";
  return viewKey;
};

const SECTION_TO_COMPONENT_KEY = {
  Greet: "G",
  Resolve: "R",
  Engage: "E",
  Assure: "A",
  Thank: "T",
};

export const DIGITAL_GREAT_COMPONENT_DRILLDOWN = {
  G: { label: "G - Greeting" },
  R: { label: "R - Resolve" },
  E: { label: "E - Engage" },
  A: { label: "A - Assure" },
  T: { label: "T - Thank" },
};

export const DIGITAL_RUBRIC = [
  { section: "Greet", fields: ["greeting_salutation", "professional_tone"] },
  {
    section: "Resolve",
    fields: [
      "identification_verification",
      "demonstrates_ownership",
      "appropriate_responses",
      "questioning",
      "checking_confirming_understanding",
      "clear_concise_explanation_fcr",
    ],
  },
  {
    section: "Engage",
    fields: ["empathy", "right_recipient", "grammar", "punctuation", "structure_formatting"],
  },
  { section: "Assure", fields: ["sounds_reassuring", "confidence"] },
  { section: "Thank", fields: ["closing", "offers_additional_assistance"] },
];

export const FIELD_LABELS = {
  greeting_salutation: "Greeting / Salutation",
  professional_tone: "Professional Tone",
  identification_verification: "Identification & Verification",
  demonstrates_ownership: "Demonstrates Ownership",
  appropriate_responses: "Appropriate Responses",
  questioning: "Questioning",
  checking_confirming_understanding: "Checking & Confirming Understanding",
  clear_concise_explanation_fcr: "Clear & Concise Explanation (FCR)",
  empathy: "Empathy",
  right_recipient: "Right Recipient",
  grammar: "Grammar",
  punctuation: "Punctuation",
  structure_formatting: "Structure & Formatting",
  sounds_reassuring: "Sounds Reassuring",
  confidence: "Confidence",
  closing: "Closing",
  offers_additional_assistance: "Offers Additional Assistance",
};

/** Sub-metrics per G/R/E/A/T for GREAT Components drill-down charts */
export const digitalGreatComponentMetricMap = Object.fromEntries(
  DIGITAL_RUBRIC.map(({ section, fields }) => [
    SECTION_TO_COMPONENT_KEY[section],
    fields.map((key) => ({ key, label: FIELD_LABELS[key] || key })),
  ])
);

export const parseInteractionDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const str = String(raw).trim();
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Drop August 2026 interactions from digital charts until data is finalized. */
export const isAugust2026Row = (row) => {
  const d = parseInteractionDate(row?.interaction_date);
  return d && d.getFullYear() === 2026 && d.getMonth() === 7;
};

export const formatDigitalSentimentLabel = (value) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (raw === "positive" || raw === "happy") return "Happy";
  if (raw === "negative" || raw === "unhappy") return "Unhappy";
  if (raw === "neutral") return "Neutral";
  if (!raw || raw === "n/a") return "N/A";
  return String(value).trim();
};

export const isSentimentMatch = (row, sentimentFilter) => {
  if (!sentimentFilter || sentimentFilter === "all") return true;
  const raw = String(row?.sentiment ?? "N/A").trim().toLowerCase();
  const want = String(sentimentFilter).trim().toLowerCase();
  if (want === "positive" && (raw === "positive" || raw === "happy")) return true;
  if (want === "negative" && (raw === "negative" || raw === "unhappy")) return true;
  if (want === "neutral") return raw === "neutral" || raw === "n/a" || raw === "";
  return raw === want;
};

export const isYesValue = (val) => {
  if (val === null || val === undefined) return false;
  const s = String(val).toLowerCase().trim();
  return s === "yes" || s === "y" || s === "1" || s === "true";
};

/** Yes/No rubric values, or numeric scores > 0, count toward pass rate in drill-down charts */
export const digitalMetricCountsAsYes = (val) => {
  if (isYesValue(val)) return true;
  const n = parseFloat(val);
  return !Number.isNaN(n) && n > 0;
};

export const isPassRow = (row) => {
  const score = parseFloat(row?.total_score);
  if (!Number.isNaN(score)) return score >= 80;
  const pf = String(row?.pass_fail ?? "").toLowerCase();
  if (pf.includes("pass")) return true;
  if (pf.includes("fail")) return false;
  return false;
};

/** Happy / Neutral / Unhappy counts for digital sentiment card (excludes N/A). */
export const summarizeDigitalSentiment = (rows) => {
  let happy = 0;
  let neutral = 0;
  let unhappy = 0;
  (rows || []).forEach((row) => {
    const label = formatDigitalSentimentLabel(row?.sentiment);
    if (label === "Happy") happy += 1;
    else if (label === "Neutral") neutral += 1;
    else if (label === "Unhappy") unhappy += 1;
  });
  const total = happy + neutral + unhappy;
  const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);
  return {
    total,
    happy,
    neutral,
    unhappy,
    happyPct: pct(happy),
    neutralPct: pct(neutral),
    unhappyPct: pct(unhappy),
  };
};

export const summarizeDigitalRows = (rows) => {
  const list = rows || [];
  const total = list.length;
  let pass = 0;
  let fail = 0;
  const scores = [];
  list.forEach((row) => {
    if (isPassRow(row)) pass += 1;
    else fail += 1;
    const s = parseFloat(row?.total_score);
    if (!Number.isNaN(s)) scores.push(s);
  });
  scores.sort((a, b) => a - b);
  const avgScore =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
      : "—";
  const medianScore =
    scores.length > 0
      ? scores.length % 2 === 1
        ? scores[(scores.length - 1) / 2]
        : (scores[scores.length / 2 - 1] + scores[scores.length / 2]) / 2
      : "—";
  const passRate = total > 0 ? Math.round((pass / total) * 1000) / 10 : 0;
  return { total, pass, fail, passRate, avgScore, medianScore };
};
